/**
 * Call sounds, generated so no audio files are needed: a ringing pattern for incoming calls
 * and short chimes when somebody comes into or leaves the call.
 */
let ctx: AudioContext | null = null;

function context(): AudioContext | null {
  try {
    ctx ??= new AudioContext();
    void ctx.resume().catch(() => undefined);
    return ctx;
  } catch {
    return null;
  }
}

/** One soft bell tone: quick attack, then fading out. */
function tone(ac: AudioContext, frequency: number, at: number, length: number, volume = 0.18) {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'sine';
  osc.frequency.value = frequency;
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(volume, at + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + length);
  osc.connect(gain).connect(ac.destination);
  osc.start(at);
  osc.stop(at + length + 0.05);
}

function ringOnce(ac: AudioContext): void {
  const t = ac.currentTime + 0.05;
  for (const [i, f] of [880, 660, 880, 660].entries()) tone(ac, f, t + i * 0.18, 0.35);
}

/** Rings every two seconds until stopped, at most `maxMs`. Returns the stop function. */
export function startRinging(maxMs = 30_000): () => void {
  const ac = context();
  if (!ac) return () => undefined;
  ringOnce(ac);
  const timer = setInterval(() => ringOnce(ac), 2000);
  const limit = setTimeout(() => clearInterval(timer), maxMs);
  return () => {
    clearInterval(timer);
    clearTimeout(limit);
  };
}

/** Somebody joined the call (rising) or left it (falling); also the hint for a running call. */
export function chime(kind: 'join' | 'leave'): void {
  const ac = context();
  if (!ac) return;
  const t = ac.currentTime + 0.03;
  const [a, b] = kind === 'join' ? [660, 990] : [990, 660];
  tone(ac, a, t, 0.25, 0.12);
  tone(ac, b, t + 0.14, 0.3, 0.12);
}
